import { useState, useEffect, useRef } from "react";
export default function WhyChooseUsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const features = [{
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></svg>,
    title: "Built for Local Businesses",
    desc: "We understand how customers search for local services and build websites that connect with your community."
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>,
    title: "Results-Focused Strategy",
    desc: "Every website and campaign is designed with one goal: generating more leads and sales for your business."
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle></svg>,
    title: "SEO Built In",
    desc: "Websites are built with strong technical SEO foundations — fast, structured, and search-engine friendly from day one."
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect><path d="M12 18h.01"></path></svg>,
    title: "Mobile-First Design",
    desc: "Perfect performance across phones, tablets, and desktops — where over 60% of local searches happen."
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path></svg>,
    title: "Transparent Communication",
    desc: "Clear reporting, straightforward recommendations, and no confusing jargon. You'll always know where you stand."
  }, {
    icon: <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"></path><path d="M7 10v12"></path></svg>,
    title: "Long-Term Partnership",
    desc: "We help businesses grow long after launch with ongoing support, optimization, and strategic guidance."
  }];
  return <section style={{
    backgroundColor: "rgb(248, 250, 252)",
    backgroundPosition: "0% 0%",
    backgroundRepeat: "repeat",
    color: "rgb(2, 8, 23)",
    fontFamily: '"DM Sans", sans-serif',
    animation: "none 0s ease 0s 1 normal none running"
  }} className="w-full">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(24px); }
          100% { opacity: 1; transform: translateY(0px); }
        }
        .feature-icon-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          min-width: 28px;
          background-color: #EFF6FF;
          border-radius: 6px;
        }
      `}</style>
      <div className="page-container" style={{
      paddingTop: "clamp(80px, 7vw, 112px)",
      paddingBottom: "clamp(80px, 7vw, 112px)"
    }}>
        <div ref={ref} className={`flex flex-col md:flex-row items-center gap-12 md:gap-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex-1 w-full" style={{
          maxWidth: "620px"
        }}>
            <div className="inline-flex items-center gap-2 rounded-full mb-4" style={{
            backgroundColor: "#EFF3F8",
            paddingTop: "8px",
            paddingBottom: "8px",
            paddingLeft: "16px",
            paddingRight: "16px"
          }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"></path><path d="M7 10v12"></path></svg>
              <span style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "14px",
              fontWeight: "500",
              color: "#64748B"
            }}>
                Why Choose CSN Digital
              </span>
            </div>

            <h2 style={{
            color: "rgb(30, 41, 59)",
            fontFamily: "Outfit, sans-serif",
            fontSize: "48px",
            fontWeight: "700",
            lineHeight: "48px",
            letterSpacing: "-1.2px",
            border: "0px solid rgb(226, 232, 240)",
            animation: "none 0s ease 0s 1 normal none running",
            marginTop: "16px",
            marginBottom: "0px"
          }}>
              We Don&apos;t Just Build Websites —{" "}
              <span style={{
              color: "#2563EB"
            }}>We Build Businesses</span>
            </h2>

            <p style={{
            color: "rgb(100, 116, 139)",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "18px",
            fontWeight: "400",
            lineHeight: "29.25px",
            border: "0px solid rgb(226, 232, 240)",
            animation: "none 0s ease 0s 1 normal none running",
            marginTop: "20px",
            marginBottom: "0px"
          }}>
              Unlike generic agencies that treat every client the same, we
              specialize in helping local service businesses attract more
              customers and grow their revenue.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 mt-9" style={{
            columnGap: "48px",
            rowGap: "36px"
          }}>
              {features.map((feature, i) => <div key={i} className="flex flex-col">
                  <div className="flex flex-row items-center gap-3 mb-1.5">
                    <div className="feature-icon-wrap">{feature.icon}</div>
                    <span style={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#1E293B",
                  lineHeight: "1.4"
                }}>
                      {feature.title}
                    </span>
                  </div>
                  <p style={{
                color: "rgb(100, 116, 139)",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22.75px",
                border: "0px solid rgb(226, 232, 240)",
                animation: "none 0s ease 0s 1 normal none running",
                marginLeft: "40px",
                marginTop: "0px",
                marginBottom: "0px"
              }}>
                    {feature.desc}
                  </p>
                </div>)}
            </div>
          </div>

          <div className="flex-1 w-full flex justify-center md:justify-end">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1710017/img/2210296635_840aee7d0e.jpg" alt="CSN Digital team collaborating on digital marketing strategy" className="w-full h-auto" style={{
            maxWidth: "568px",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
            display: "block"
          }} />
          </div>
        </div>
      </div>
    </section>;
}